/**
 * Viewport Helper Utilities — Phase 2
 * Convert between screen and canvas coordinates.
 *
 * The visible canvas stops where the docked Partnr panel starts: its width is
 * `rightInset` in the panel store (0 when it is closed or a bottom sheet), so
 * items placed at the "center" land beside the panel, not under it.
 */
import { usePartnrPanelStore } from '@/stores/partnrPanelStore';

/** Pixels of the window the open Partnr panel covers on the right. */
export function getRightInset(): number {
  return usePartnrPanelStore.getState().rightInset;
}

/** Get the center point of the current viewport in canvas coordinates. */
export function getViewportCenter(viewportState: {
  panX: number;
  panY: number;
  zoom: number;
  /** Defaults to the open panel's width. */
  rightInset?: number;
}): { x: number; y: number } {
  const { panX, panY, zoom } = viewportState;
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const windowHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;
  const visibleWidth = Math.max(0, windowWidth - (viewportState.rightInset ?? getRightInset()));

  return {
    x: (-panX + visibleWidth / 2) / zoom,
    y: (-panY + windowHeight / 2) / zoom,
  };
}

/** Get the viewport bounds in canvas coordinates. */
export function getViewportBounds(
  panX: number,
  panY: number,
  zoom: number,
  windowWidth?: number,
  windowHeight?: number,
  rightInset: number = getRightInset(),
): { left: number; top: number; right: number; bottom: number } {
  const w = windowWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 1920);
  const h = windowHeight ?? (typeof window !== 'undefined' ? window.innerHeight : 1080);

  return {
    left: -panX / zoom,
    top: -panY / zoom,
    right: (-panX + Math.max(0, w - rightInset)) / zoom,
    bottom: (-panY + h) / zoom,
  };
}
